/**
 * @file SupplierCreditPanel.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-08
 * @modified 2026-10-13
 *
 * The bill window's supplier credit section (Cam, 8 Oct 2026). On a credit:
 * what it relates to (its POs and bills — a reference only, never billing)
 * and the bills it is allocated to. On a bill: the credits that name it and
 * the credits allocated to it. Read-only; allocating and removing are in
 * SupplierCreditAllocationModal.
 */

import React, { useEffect, useState } from 'react';
import { API_URL } from '../config';
import EntityLink from './EntityLink';
import { formatCurrency } from '../utils/currencyFormatter';
import { formatDateShort } from '../utils/dateFormatters';
import { isSupplierCredit, autoAllocationText } from '../utils/supplierCredits';
import './SupplierCreditAllocationModal.css';

const labelOf = (r) => r?.supplier_reference || r?.bill_number || (r?.id ? `#${r.id}` : '');

/**
 * @param {object} props
 * @param {object} props.bill — GET /api/bills/:id's bill (credit_links / related_credits)
 * @param {any} [props.refreshKey] — reloads the allocations when it changes
 */
function SupplierCreditPanel({ bill, refreshKey }) {
  const [allocs, setAllocs] = useState(null);
  const credit = isSupplierCredit(bill);
  useEffect(() => {
    if (!bill?.id) return undefined;
    let cancelled = false;
    fetch(`${API_URL}/api/bills/${bill.id}/credit-allocations`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (!cancelled) setAllocs(d.success ? d : { ready: false, allocations: [] }); })
      .catch(() => { if (!cancelled) setAllocs({ ready: false, allocations: [] }); });
    return () => { cancelled = true; };
  }, [bill?.id, refreshKey]);

  const links = bill?.credit_links || { pos: [], bills: [] };
  const autoAlloc = bill?.xero_push_info?.auto_allocation || null;
  const related = bill?.related_credits || [];
  const list = allocs?.allocations || [];
  if (!credit && !related.length && !list.length) return null;
  return (
    <div className="detail-section supplier-credit-panel">
      <h3>{credit ? 'Credit' : 'Supplier credits'}</h3>
      {credit && (
        <>
          <p className="scp-sub">Relates to (a reference only — a credit never bills a PO or changes a supplied quantity):</p>
          {(links.pos.length || links.bills.length) ? (
            <ul>
              {links.pos.map(p => (
                <li key={`po-${p.id}`}>
                  <EntityLink type="purchase_order" id={p.xero_purchase_order_id}><strong>{p.purchase_order_number}</strong></EntityLink>
                  {p.status ? ` · ${p.status}` : ''}
                </li>
              ))}
              {links.bills.map(b => (
                <li key={`bill-${b.id}`}>
                  <EntityLink type="bill" id={b.id}><strong>{labelOf(b)}</strong></EntityLink>
                  {b.bill_date ? ` · ${formatDateShort(b.bill_date)}` : ''}
                </li>
              ))}
            </ul>
          ) : <p className="scp-empty">No PO or bill named.</p>}
        </>
      )}
      {!credit && related.length > 0 && (
        <>
          <p className="scp-sub">Credits that name this bill:</p>
          <ul>
            {related.map(c => (
              <li key={`rc-${c.id}`}>
                <EntityLink type="bill" id={c.id}><strong>{labelOf(c)}</strong></EntityLink>
                {` · ${formatCurrency(c.total)}`}{c.bill_date ? ` · ${formatDateShort(c.bill_date)}` : ''}
              </li>
            ))}
          </ul>
        </>
      )}
      {/* A credit raised against one bill (2026-10-13): its automatic
          allocation still to come, or why it was not made. */}
      {credit && ['pending', 'failed', 'cancelled'].includes(autoAlloc?.status) && (
        <p className={`scp-auto scp-auto--${autoAlloc.status}`}>{autoAllocationText(autoAlloc)}</p>
      )}
      <p className="scp-sub">{credit ? 'Allocated to:' : 'Credits allocated to it:'}</p>
      {allocs && allocs.ready === false ? (
        <p className="scp-empty">Allocations are recorded once the supplier credits migration has run.</p>
      ) : list.length ? (
        <ul>
          {list.map(a => (
            <li key={`a-${a.id}`}>
              <EntityLink type="bill" id={credit ? a.bill_id : a.credit_bill_id}>
                <strong>{credit ? (a.bill_reference || a.bill_number) : (a.credit_reference || a.credit_number)}</strong>
              </EntityLink>
              {` · ${formatCurrency(a.allocated_amount)} · ${formatDateShort(a.allocation_date)}`}
              {a.source === 'xero' ? ' · made in Xero' : ''}
            </li>
          ))}
        </ul>
      ) : <p className="scp-empty">None.</p>}
    </div>
  );
}

export default SupplierCreditPanel;
